/**
 * Забежка (E4): переходы между волнами. Чистые функции над сериализуемым `RunState` (contracts/wave-log) —
 * состояние хранит сервер (S2/S3), клиент считает то же самое сразу, не дожидаясь ответа (I2).
 *
 * - Победа: следующая волна, взятое усиление +1 стак, штаб чинится между волнами ({@link RunOptions.hqRepairPermille}).
 * - Проигрыш: откат на `defeatRollback` волн (не ниже 1 и пройденного босса), усиления сбрасываются, штаб — полный,
 *   новая забежка.
 * - Выход (закрыл игру / свёрнуто > 10 мин — решает сервер): усиления сбрасываются, волна та же, новая забежка.
 *
 * Монеты волны — `WaveResult.coins` (считает бой через `economy.waveReward`, `coinGain` один раз); забежка их не
 * пересчитывает, а только возвращает для кошелька.
 */
import { PERMILLE, type BalanceData, type RunState, type WaveLog, type WaveResult } from "@newfungame/contracts";
import { replayWaveLog } from "../sim/replay";
import type { BattleHooks } from "../sim/battle";
import { mulPermille } from "../sim/math";
import { collectRunModifiers, hqStats } from "../sim/stats";
import { bossForWave, rollbackWave } from "../sim/waves";
import { addBoost, boostOffer, checkBoostChoice, type BoostChoiceRejection, type BoostOption } from "./offer";

/**
 * Ремонт штаба между пройденными волнами, промилле от максимума после выбора усиления. 1000 — полная прочность на
 * старте каждой волны (как считает симулятор баланса); меньше — урон копится по забежке. Решение по балансу — E2/E6.
 */
export const HQ_REPAIR_PERMILLE = PERMILLE;

export interface RunOptions {
  /** Новый `runId` для забежки после проигрыша или выхода (выдаёт сервер). Нет — остаётся прежний. */
  runId?: string;
  /** Ремонт штаба между волнами, промилле (по умолчанию {@link HQ_REPAIR_PERMILLE}). */
  hqRepairPermille?: number;
  /** Лучшая пройденная волна игрока: пройденный босс — чекпойнт отката. */
  bestWave?: number;
}

/** Максимум прочности штаба для состояния забежки (Tech и усиления). */
export function runHqMax(balance: BalanceData, run: RunState): number {
  return hqStats(balance, collectRunModifiers(balance, run)).hpMax;
}

/** Волна, которая начнётся из этого состояния: номер, босс, открыты ли «Автонавыки». */
export interface UpcomingWave {
  wave: number;
  isBoss: boolean;
  bossId: string | null;
  autoSkillsUnlocked: boolean;
}

/** Следующая волна из `RunState` — для клиента (экран перед боем) и сервера (`NextWave`); сид выдаёт сервер. */
export function upcomingWave(balance: BalanceData, run: Pick<RunState, "wave">): UpcomingWave {
  const bossId = bossForWave(balance, run.wave);
  return {
    wave: run.wave,
    isBoss: bossId !== null,
    bossId,
    autoSkillsUnlocked: run.wave >= balance.waves.autoSkillsFromWave,
  };
}

/** Начало новой забежки на волне `wave` (или текущей): без усилений, штаб полный. Вход не мутируется. */
export function restartRun(balance: BalanceData, run: RunState, wave = run.wave, opts: RunOptions = {}): RunState {
  const next: RunState = {
    ...run,
    runId: opts.runId ?? run.runId,
    wave: Math.max(1, wave),
    heroes: run.heroes.map((h) => ({ ...h })),
    tech: { ...run.tech },
    boosts: [],
    hqHp: 0,
  };
  next.hqHp = runHqMax(balance, next);
  return next;
}

/** Выход из забежки (закрыл / свёрнуто > 10 мин): усиления сбрасываются, волна остаётся, штаб полный. */
export function exitRun(balance: BalanceData, run: RunState, opts: RunOptions = {}): RunState {
  return restartRun(balance, run, run.wave, opts);
}

/** Итог волны для забежки. */
export type WaveSettlement =
  | {
      ok: true;
      outcome: "cleared" | "lost";
      /** Состояние на старте следующей волны. */
      run: RunState;
      /** Монеты волны в кошелёк (= `WaveResult.coins`). */
      coins: number;
      /** Предложение «1 из 3» после этой волны (пусто после проигрыша). */
      offer: BoostOption[];
      /** Взятое усиление или `null`. */
      boost: BoostOption | null;
      /** Куда откатилась забежка после проигрыша, иначе `null`. */
      rollbackTo: number | null;
    }
  | { ok: false; code: BoostChoiceRejection };

export interface SettleInput {
  /** Состояние на старте волны. */
  run: RunState;
  /** Сид волны. */
  seed: number;
  /** Итог волны (свой — у клиента, пересчитанный — у сервера). */
  result: WaveResult;
  /** Команды волны (журнал); из них берётся `chooseBoost`. */
  commands: WaveLog["commands"];
}

/**
 * Закрыть волну: проверить выбор усиления и перейти к следующей волне (победа) или откатиться (проигрыш).
 * Недопустимый выбор (не из предложения, второй выбор, выбор после проигрыша) — `ok: false`, состояние не меняется.
 */
export function settleWave(balance: BalanceData, input: SettleInput, opts: RunOptions = {}): WaveSettlement {
  const { run, seed, result } = input;
  const check = checkBoostChoice(balance, run, seed, result.outcome, input.commands);
  if (!check.ok) return check;

  if (result.outcome === "lost") {
    const to = rollbackWave(balance, run.wave, opts.bestWave);
    return {
      ok: true,
      outcome: "lost",
      run: restartRun(balance, run, to, opts),
      coins: result.coins,
      offer: [],
      boost: null,
      rollbackTo: to,
    };
  }

  const offer = boostOffer(balance, run, seed);
  const next: RunState = {
    ...run,
    wave: run.wave + 1,
    heroes: run.heroes.map((h) => ({ ...h })),
    tech: { ...run.tech },
    boosts: check.boost ? addBoost(balance, run.boosts, check.boost.boostId) : run.boosts.map((b) => ({ ...b })),
  };
  const max = runHqMax(balance, next);
  const repair = mulPermille(max, Math.max(0, opts.hqRepairPermille ?? HQ_REPAIR_PERMILLE));
  next.hqHp = Math.min(max, result.hqHp + repair);
  return { ok: true, outcome: "cleared", run: next, coins: result.coins, offer, boost: check.boost, rollbackTo: null };
}

/** Итог проверки журнала волны сервером: пересчитанный итог и переход забежки. */
export type WaveLogSettlement = WaveSettlement & { result: WaveResult };

/**
 * Серверный путь (S2): прогнать журнал на своём состоянии забежки и своём сиде, затем {@link settleWave}.
 * Сравнение `result` с присланным (`diffWaveResults`) — на стороне вызывающего.
 */
export function settleWaveLog(
  balance: BalanceData,
  log: WaveLog,
  server: { run?: RunState; seed?: number; hooks?: BattleHooks; autoSkillsAllowed?: boolean } = {},
  opts: RunOptions = {},
): WaveLogSettlement {
  const run = server.run ?? log.start;
  const seed = server.seed ?? log.seed;
  const result = replayWaveLog(log, balance, {
    run,
    seed,
    hooks: server.hooks,
    ...(server.autoSkillsAllowed !== undefined ? { autoSkillsAllowed: server.autoSkillsAllowed } : {}),
  });
  return { ...settleWave(balance, { run, seed, result, commands: log.commands }, opts), result };
}
